import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { CheckCircle2 } from 'lucide-react'

import {
  ApiError,
  type ExplorerTxRecord,
  api,
  getTxExtraDataJson,
  getTxDisplayAction,
  getTxDisplayAmount,
  getTxYfvFields,
  isAbortError,
} from '@/shared/api/client'
import { useIsMobile } from '@/shared/hooks/useIsMobile'
import { formatUTC, truncateMiddle } from '@/shared/lib/format'
import { isAddress, isTxHash } from '@/shared/lib/validators'
import ActionBadge from '@/shared/ui/ActionBadge'
import CopyButton from '@/shared/ui/CopyButton'
import DetailRow from '@/shared/ui/DetailRow'
import HashLink from '@/shared/ui/HashLink'
import InfoCard from '@/shared/ui/InfoCard'
import { ErrorView, Loading } from '@/shared/ui/StateView'

const TransactionDetails: React.FC = () => {
  const { hash } = useParams<{ hash: string }>()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const [tx, setTx] = useState<ExplorerTxRecord | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryToken, setRetryToken] = useState(0)

  useEffect(() => {
    if (!hash) return
    if (!isTxHash(hash)) {
      navigate('/404', { replace: true })
      return
    }

    const ctrl = new AbortController()
    let cancelled = false
    setLoading(true)
    setError(null)

    api
      .getTransaction(hash, { signal: ctrl.signal })
      .then((data) => {
        if (cancelled) return
        if (!data) {
          navigate('/404', { replace: true })
          return
        }
        setTx(data)
      })
      .catch((err: unknown) => {
        if (cancelled || isAbortError(err)) return
        if (err instanceof ApiError && err.isNotFound) {
          navigate('/404', { replace: true })
          return
        }
        setError(err instanceof Error ? err.message : 'Failed to load transaction details')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
      ctrl.abort()
    }
  }, [hash, navigate, retryToken])

  const extraData = useMemo(() => {
    if (!tx) return { label: 'Extra Data', value: '' }
    return getTxExtraDataJson(tx)
  }, [tx])

  const yfvFields = useMemo(() => {
    if (!tx) return []
    return getTxYfvFields(tx)
  }, [tx])

  if (loading) return <Loading />
  if (error) return <ErrorView message={error} onRetry={() => setRetryToken((t) => t + 1)} />
  if (!tx) return <ErrorView message="Transaction not found." />

  const showReceiver = tx.biz_type !== 'DEPOSIT' && !!tx.to_address && tx.to_address.trim() !== ''
  const receiverIsValid = showReceiver && isAddress(tx.to_address!)
  const senderIsValid = isAddress(tx.wallet_address)

  const valueStyle: React.CSSProperties = {
    color: 'var(--app-text-primary)',
    fontSize: isMobile ? 'var(--font-size-md)' : 'var(--font-size-base)',
    wordBreak: 'break-all',
    minWidth: 0,
  }

  return (
    <div style={{ width: '100%' }}>
      <h2
        style={{
          marginBottom: isMobile ? 'var(--space-lg)' : 'var(--page-heading-margin-bottom-desktop)',
          fontWeight: 600,
          letterSpacing: 'var(--tracking-title)',
          fontSize: isMobile ? 'var(--font-size-h3)' : 'var(--font-size-h2)',
        }}
      >
        Transaction Information
      </h2>

      <InfoCard
        style={{
          padding: isMobile ? 'var(--space-14) var(--space-lg)' : 'var(--space-3xl) var(--space-40)',
          overflow: isMobile ? 'hidden' : 'visible',
        }}
      >
        <DetailRow label="Hash">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', minWidth: 0 }}>
            {isMobile ? (
              <Link
                to={`/tx/${tx.tx_hash}`}
                style={{
                  color: 'var(--accent-cyan)',
                  fontFamily: 'monospace',
                  fontSize: 'var(--font-size-md)',
                  textDecoration: 'none',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {truncateMiddle(tx.tx_hash)}
              </Link>
            ) : (
              <span
                style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-base)', wordBreak: 'break-all', flex: 1 }}
              >
                {tx.tx_hash}
              </span>
            )}
            <CopyButton text={tx.tx_hash} label="Copy transaction hash" />
          </div>
        </DetailRow>

        <DetailRow label="Status">
          <div
            style={{
              color: 'var(--app-up)',
              fontSize: isMobile ? 'var(--font-size-md)' : 'var(--font-size-base)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-md)',
            }}
          >
            <CheckCircle2 size={isMobile ? 14 : 16} />
            <span>Completed</span>
          </div>
        </DetailRow>

        <DetailRow label="Time">
          <span style={{ ...valueStyle, whiteSpace: 'nowrap' }}>{formatUTC(tx.block_time)}</span>
        </DetailRow>

        <DetailRow label="Block">
          <HashLink kind="block" value={tx.block_height} copyable={false} fontSize={isMobile ? 13 : 15} />
        </DetailRow>

        {isMobile && <div style={{ height: '1px', backgroundColor: 'var(--app-divider)', margin: 0 }} />}

        <DetailRow label={isMobile ? 'From' : 'Address'}>
          {senderIsValid ? (
            <HashLink kind="address" value={tx.wallet_address} truncate={isMobile} fontSize={isMobile ? 13 : 15} />
          ) : (
            <span style={{ ...valueStyle, fontFamily: 'monospace' }}>
              {isMobile ? truncateMiddle(tx.wallet_address) : tx.wallet_address}
            </span>
          )}
        </DetailRow>

        {showReceiver && (
          <DetailRow label="To">
            {receiverIsValid ? (
              <HashLink kind="address" value={tx.to_address!} truncate={isMobile} fontSize={isMobile ? 13 : 15} />
            ) : (
              <span style={{ ...valueStyle, fontFamily: 'monospace' }}>
                {isMobile ? truncateMiddle(tx.to_address!) : tx.to_address}
              </span>
            )}
          </DetailRow>
        )}

        <DetailRow label="Action">
          <ActionBadge
            label={getTxDisplayAction(tx)}
            fontSize={isMobile ? 'var(--font-size-xs)' : 'var(--font-size-lg)'}
            padding={isMobile ? 'var(--space-xxs) var(--space-md)' : 'var(--action-badge-h-padding) var(--space-lg)'}
            style={{ display: 'inline-flex' }}
          />
        </DetailRow>

        <DetailRow label="Amount">
          <span style={{ ...valueStyle, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
            {tx.biz_type === 'TRANSFER' ? getTxDisplayAmount(tx, 999).replace(/^[+-]/, '') : getTxDisplayAmount(tx, 999)}
          </span>
        </DetailRow>

        {yfvFields.map((row, index) => (
          <DetailRow key={row.label} label={row.label} hideDivider={!extraData.value && index === yfvFields.length - 1}>
            <span style={{ ...valueStyle, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{row.value}</span>
          </DetailRow>
        ))}

        {extraData && extraData.value && (
          <DetailRow label="Extra Data" hideDivider>
            <pre style={{
              ...valueStyle,
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              margin: 0,
              fontSize: isMobile ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
              lineHeight: '1.6',
            }}>
              {extraData.value}
            </pre>
          </DetailRow>
        )}
      </InfoCard>
    </div>
  )
}

export default TransactionDetails
